import { useState } from "react";
import { format, isValid, parse, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { signal } from "@preact/signals-react";
import { useIntl } from "react-intl";

import { CalendarIcon } from "components/Icons";
import Calendar from "componentsv2/widgets/Calendar";
import { cn } from "tools/ui";
import { theme } from "theme/theme";

const FMT_JS = "dd/MM/yyyy";
const regex = /^[0-9/]+$/;

export default function DateFilter(props) {
  const {
    name,
    default: defaultValue = "",
    placeholder = "dd/MM/yyyy",
    value = "",
    onChange,
  } = props;

  const intl = useIntl();
  // Obtener texto del placeholder como string (cae al mismo placeholder si no hay traducción)
  const placeholderText =
    typeof placeholder === "string"
      ? intl.formatMessage({ id: placeholder, defaultMessage: placeholder })
      : String(placeholder);

  const showPop = signal(false);
  const [month, setMonth] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const val = value ?? defaultValue;
    if (!val) return null;
    const maybe = typeof val === "object" && "value" in val ? val.value : val;

    if (typeof maybe === "string") {
      try {
        const parsed = parseISO(maybe);
        return isValid(parsed) ? parsed : null;
      } catch {
        return null;
      }
    }

    if (maybe instanceof Date) {
      return isValid(maybe) ? maybe : null;
    }

    return null;
  });

  const [fmtDate, setFmtDate] = useState(() =>
    selectedDate ? format(selectedDate, FMT_JS, { locale: es }) : "",
  );

  function handlePop() {
    showPop.value = true;
  }

  function handleInputChange(e) {
    const v = e.target.value;
    if (v.length > 10 || (v !== "" && !regex.test(v))) return;
    setFmtDate(v);
  }

  function handleBlur() {
    const parsed = parse(fmtDate, FMT_JS, new Date());
    if (isValid(parsed)) {
      setSelectedDate(parsed);
      setMonth(parsed);
      notifyChange(parsed);
    } else {
      setFmtDate("");
      setSelectedDate(null);
      notifyChange(null);
    }
  }

  function handleDateSelect(date) {
    if (!date) return;
    setSelectedDate(date);
    setMonth(date);
    setFmtDate(format(date, FMT_JS, { locale: es }));
    notifyChange(date);
    showPop.value = false;
  }

  function notifyChange(date) {
    if (typeof onChange === "function") onChange(name, date);
  }

  return (
    <div className="relative w-full">
      <div className="flex px-3 h-10.5 justify-between shadow-sm border border-gray-200 rounded-xl bg-white">
        <input
          type="text"
          value={fmtDate}
          onChange={handleInputChange}
          onBlur={handleBlur}
          className={cn(theme.textColor)}
          placeholder={placeholderText} // <-- aquí va una string
        />

        <div
          className="absolute right-2 top-3 cursor-pointer"
          onClick={handlePop}
        >
          <CalendarIcon className="text-neutral-300 size-5" />
        </div>
      </div>

      <Calendar
        mode="single"
        month={month || selectedDate}
        selected={selectedDate}
        onSelect={handleDateSelect}
        show={showPop}
        initialFocus
      />
    </div>
  );
}
